import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import type { OnChangeValue } from 'react-select';

import Modal from '@/components/atoms/Modal';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Form/Input';
import MultiSelect from '@/components/atoms/MultiSelect';
import type { SelectOptionInterface } from '@/components/atoms/MultiSelect/MultiSelect.interface';
import { getUserToken } from '@/redux/user';
import { computeText } from '@/locales/utils';
import { SVGIcon, SVGIcons } from '@/components/atoms/Icon/SVGIcon';
import {
    useCreateBrandPromotionalBannerMutation,
    useUpdateBrandPromotionalBannerMutation,
    useToggleBrandPromotionalBannerMutation,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type {
    BrandPromotionalBanner,
    LinkType,
    BrandPromotionalBannerTarget,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import type { PickupScheduleSlot } from '@/services/innovorder/restaurant/restaurant.type';
import { ChannelId } from '@/services/innovorder/channel/channel.type';

import { BannerImageSection } from '@/pages/PromotionalBanners/components/BannerImageSection/BannerImageSection';
import { ConfirmReloadModal } from '@/pages/PromotionalBanners/components/ConfirmReloadModal/ConfirmReloadModal';
import { isInServiceHours } from '@/pages/PromotionalBanners/utils/isInServiceHours';
import {
    SectionLabel,
    HelperText,
    AlertText,
    RadioCardGroup,
    RadioCard,
    RadioCardContent,
    RadioCardTitle,
    RadioCardDescription,
    RadioDot,
    ChannelCheck,
    DisabledInfo,
} from '@/pages/PromotionalBanners/BannerModal.style';

type FormValues = {
    name: string;
    imageId: number | null;
    linkType: LinkType;
    linkTargetRef: string;
    restaurantIds: number[];
};

type Props = {
    isOpen: boolean;
    mode: 'create' | 'edit';
    banner: BrandPromotionalBanner | null;
    brandId: number;
    restaurants: BrandPromotionalBannerTarget[];
    schedulesMap: Record<number, PickupScheduleSlot[]>;
    onHide: () => void;
};

export const BrandPromotionalBannerModal: React.FC<Props> = ({
    isOpen,
    mode,
    banner,
    brandId,
    restaurants,
    schedulesMap,
    onHide,
}) => {
    const isEdit = mode === 'edit';
    const isActiveBanner = isEdit && banner?.isActive;
    const token = useSelector(getUserToken) as string;
    const intl = useIntl();

    const [createBanner] = useCreateBrandPromotionalBannerMutation();
    const [updateBanner] = useUpdateBrandPromotionalBannerMutation();
    const [toggleBanner] = useToggleBrandPromotionalBannerMutation();

    const [showConfirmReload, setShowConfirmReload] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [confirmRestaurantCount, setConfirmRestaurantCount] = useState(0);

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        formState: { isDirty, isValid },
    } = useForm<FormValues>({
        defaultValues: {
            name: '',
            imageId: null,
            linkType: 'none',
            linkTargetRef: '',
            restaurantIds: [],
        },
    });

    const [currentLinkType, setCurrentLinkType] = useState<LinkType>(banner?.linkType ?? 'none');

    useEffect(() => {
        if (banner && isEdit) {
            const existingRestaurantIds = (banner.promotional_banner_brands ?? []).map((pbr) => pbr.restaurantId);
            reset({
                name: banner.name,
                imageId: banner.imageId,
                linkType: banner.linkType,
                linkTargetRef: banner.linkTargetRef ?? '',
                restaurantIds: existingRestaurantIds,
            });
            setCurrentLinkType(banner.linkType);
        } else {
            reset({ name: '', imageId: null, linkType: 'none', linkTargetRef: '', restaurantIds: [] });
            setCurrentLinkType('none');
        }
    }, [banner, isEdit, reset]);

    useEffect(() => {
        if (!isOpen) {
            reset({ name: '', imageId: null, linkType: 'none', linkTargetRef: '', restaurantIds: [] });
            setCurrentLinkType('none');
        }
    }, [isOpen, reset]);

    const selectedRestaurantIds = watch('restaurantIds');
    const hasSelectedRestaurants = selectedRestaurantIds.length > 0;

    const withServiceHoursCheck = useCallback(
        (action: () => void, restIds: number[]) => {
            const restaurantsInService = restIds.filter((rid) => {
                const schedules = schedulesMap[rid] ?? [];
                return isInServiceHours(schedules);
            });

            if (restaurantsInService.length > 0) {
                setConfirmRestaurantCount(restaurantsInService.length);
                setPendingAction(() => action);
                setShowConfirmReload(true);
            } else {
                action();
            }
        },
        [schedulesMap],
    );

    const handleConfirmReload = useCallback(() => {
        if (pendingAction) {
            pendingAction();
        }
        setShowConfirmReload(false);
        setPendingAction(null);
    }, [pendingAction]);

    const handleCancelReload = useCallback(() => {
        setShowConfirmReload(false);
        setPendingAction(null);
    }, []);

    const submitCreate = useCallback(
        async (data: FormValues, isActive: boolean) => {
            if (!data.imageId) return;
            await createBanner({
                token,
                brandId,
                name: data.name,
                imageId: data.imageId,
                isActive,
                linkType: data.linkType,
                linkTargetRef: data.linkType === 'none' ? null : data.linkTargetRef || null,
                channelId: ChannelId.KIOSK,
                restaurantIds: data.restaurantIds,
            });
            onHide();
        },
        [token, brandId, createBanner, onHide],
    );

    const submitUpdate = useCallback(
        async (data: FormValues, activate?: boolean) => {
            if (!banner) return;
            const existingRestaurantIds = (banner.promotional_banner_brands ?? []).map((pbr) => pbr.restaurantId);
            const restaurantIdsToAdd = data.restaurantIds.filter((id) => !existingRestaurantIds.includes(id));
            const restaurantIdsToRemove = existingRestaurantIds.filter((id) => !data.restaurantIds.includes(id));

            await updateBanner({
                token,
                brandId,
                promotionalBannerId: banner.promotionalBannerId,
                name: data.name,
                imageId: data.imageId ?? undefined,
                linkType: data.linkType,
                linkTargetRef: data.linkType === 'none' ? null : data.linkTargetRef || null,
                restaurantIdsToAdd: restaurantIdsToAdd.length > 0 ? restaurantIdsToAdd : undefined,
                restaurantIdsToRemove: restaurantIdsToRemove.length > 0 ? restaurantIdsToRemove : undefined,
            });
            if (activate && !banner.isActive) {
                await toggleBanner({
                    token,
                    brandId,
                    promotionalBannerId: banner.promotionalBannerId,
                    isActive: true,
                });
            }
            onHide();
        },
        [token, brandId, banner, updateBanner, toggleBanner, onHide],
    );

    const onSubmitActive = handleSubmit((data) => {
        const doSubmit = () => {
            if (isEdit) {
                submitUpdate(data, true);
            } else {
                submitCreate(data, true);
            }
        };
        const willTouchKiosk = hasSelectedRestaurants;
        if (willTouchKiosk) {
            withServiceHoursCheck(doSubmit, data.restaurantIds);
        } else {
            doSubmit();
        }
    });

    const onSubmitDraft = handleSubmit((data) => {
        if (isEdit) {
            submitUpdate(data);
        } else {
            submitCreate(data, false);
        }
    });

    const onDeactivate = useCallback(() => {
        if (!banner) return;
        const doDeactivate = async () => {
            await toggleBanner({
                token,
                brandId,
                promotionalBannerId: banner.promotionalBannerId,
                isActive: false,
            });
            onHide();
        };
        const restIds = (banner.promotional_banner_brands ?? []).map((pbr) => pbr.restaurantId);
        withServiceHoursCheck(doDeactivate, restIds);
    }, [token, brandId, banner, toggleBanner, onHide, withServiceHoursCheck]);

    const buttons = useMemo(() => {
        if (isActiveBanner) {
            return (
                <>
                    <Button buttonType="outlinedPrimary" onClick={onDeactivate}>
                        <FormattedMessage id="brandPromotionalBanner.modal.button.deactivate" />
                    </Button>
                    <Button buttonType="primary" onClick={onSubmitActive} disabled={!isDirty}>
                        <FormattedMessage id="brandPromotionalBanner.modal.button.update" />
                    </Button>
                </>
            );
        }
        if (isEdit) {
            return (
                <>
                    <Button buttonType="outlinedPrimary" onClick={onSubmitDraft}>
                        <FormattedMessage id="brandPromotionalBanner.modal.button.saveDraft" />
                    </Button>
                    <Button buttonType="primary" onClick={onSubmitActive} disabled={!hasSelectedRestaurants}>
                        <FormattedMessage id="brandPromotionalBanner.modal.button.activate" />
                    </Button>
                </>
            );
        }
        return (
            <>
                <Button buttonType="outlinedPrimary" onClick={onSubmitDraft}>
                    <FormattedMessage id="brandPromotionalBanner.modal.button.saveDraft" />
                </Button>
                <Button buttonType="primary" onClick={onSubmitActive} disabled={!isValid || !hasSelectedRestaurants}>
                    <FormattedMessage id="brandPromotionalBanner.modal.button.activate" />
                </Button>
            </>
        );
    }, [isActiveBanner, isEdit, isDirty, isValid, hasSelectedRestaurants, onSubmitActive, onSubmitDraft, onDeactivate]);

    const linkTypeOptions: { value: LinkType; labelKey: string; descKey: string }[] = [
        {
            value: 'none',
            labelKey: 'brandPromotionalBanner.modal.link.none',
            descKey: 'promotionalBanner.modal.linkNoneDesc',
        },
        {
            value: 'product',
            labelKey: 'brandPromotionalBanner.modal.link.product',
            descKey: 'promotionalBanner.modal.linkProductDesc',
        },
        {
            value: 'category',
            labelKey: 'brandPromotionalBanner.modal.link.category',
            descKey: 'promotionalBanner.modal.linkCategoryDesc',
        },
    ];

    const restaurantOptions: SelectOptionInterface<number>[] = useMemo(
        () =>
            restaurants.map((r) => ({
                value: r.restaurantId,
                label: r.name,
            })),
        [restaurants],
    );

    const ineligibleRestaurantIds = useMemo(
        () =>
            new Set(restaurants.filter((r) => !r.hasKioskChannel || !r.hasActiveKioskMenu).map((r) => r.restaurantId)),
        [restaurants],
    );

    const isOptionDisabled = useCallback(
        (option: SelectOptionInterface<number>) => ineligibleRestaurantIds.has(option.value),
        [ineligibleRestaurantIds],
    );

    const formatOptionLabel = useCallback(
        (option: SelectOptionInterface<number>) => {
            const restaurant = restaurants.find((r) => r.restaurantId === option.value);
            if (!restaurant) return option.label;

            if (!restaurant.hasKioskChannel) {
                return `${option.label} — ${computeText(
                    intl,
                    'brandPromotionalBanner.modal.restaurants.ineligible.noChannel',
                )}`;
            }
            if (!restaurant.hasActiveKioskMenu) {
                return `${option.label} — ${computeText(
                    intl,
                    'brandPromotionalBanner.modal.restaurants.ineligible.noMenu',
                )}`;
            }
            return option.label;
        },
        [restaurants, intl],
    );

    const selectedRestaurantOptions = useMemo(
        () => restaurantOptions.filter((o) => selectedRestaurantIds.includes(o.value)),
        [restaurantOptions, selectedRestaurantIds],
    );

    const handleRestaurantChange = useCallback(
        (options: OnChangeValue<SelectOptionInterface<number>, true>) => {
            const ids = (options ?? []).map((o) => o.value);
            setValue('restaurantIds', ids, { shouldDirty: true });
        },
        [setValue],
    );

    const handleImageChange = useCallback(
        (newImageId: number | null) => {
            setValue('imageId', newImageId, { shouldDirty: true });
        },
        [setValue],
    );

    return (
        <>
            <ConfirmReloadModal
                isOpen={showConfirmReload}
                restaurantCount={confirmRestaurantCount}
                onConfirm={handleConfirmReload}
                onCancel={handleCancelReload}
            />
            <Modal
                isOpen={isOpen}
                onHide={onHide}
                title={isEdit ? 'brandPromotionalBanner.modal.editTitle' : 'brandPromotionalBanner.modal.createTitle'}
                buttons={buttons}
                width={720}
            >
                {/* Name */}
                <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <div>
                            <Input {...field} labelId="brandPromotionalBanner.modal.name.label" required />
                        </div>
                    )}
                />

                {/* Channel */}
                <SectionLabel required>
                    <FormattedMessage id="brandPromotionalBanner.modal.channel.label" />
                </SectionLabel>
                <RadioCardGroup>
                    <RadioCard selected>
                        <ChannelCheck>&#10003;</ChannelCheck>
                        <RadioCardContent>
                            <RadioCardTitle selected>
                                {computeText(intl, 'promotionalBanner.modal.channelKiosk')}
                            </RadioCardTitle>
                            <RadioCardDescription>
                                {computeText(intl, 'promotionalBanner.modal.channelKioskDesc')}
                            </RadioCardDescription>
                        </RadioCardContent>
                    </RadioCard>
                    <RadioCard disabled>
                        <RadioCardContent>
                            <RadioCardTitle>{computeText(intl, 'promotionalBanner.modal.channelWeb')}</RadioCardTitle>
                            <RadioCardDescription>
                                {computeText(intl, 'promotionalBanner.modal.channelWebDesc')}
                            </RadioCardDescription>
                            <DisabledInfo>
                                <SVGIcon icon={SVGIcons.INFO} size={14} />
                                {computeText(intl, 'brandPromotionalBanner.modal.channel.comingSoon')}
                            </DisabledInfo>
                        </RadioCardContent>
                    </RadioCard>
                </RadioCardGroup>

                {/* Image */}
                <SectionLabel required>
                    <FormattedMessage id="brandPromotionalBanner.modal.image.label" />
                </SectionLabel>
                <Controller
                    name="imageId"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <BannerImageSection
                            brandId={brandId}
                            imageId={field.value}
                            defaultImage={
                                banner?.image
                                    ? { fullUrl: banner.image.fullUrl, imageId: banner.image.imageId }
                                    : undefined
                            }
                            imageSpecsMessageId="brandPromotionalBanner.modal.image.hint"
                            imageDescriptionMessageId="promotionalBanner.modal.imageFileDescription"
                            replaceMessageId="promotionalBanner.modal.replace"
                            errorRatioMessageId="brandPromotionalBanner.modal.image.error.ratio"
                            onChange={(newImageId) => {
                                handleImageChange(newImageId);
                                field.onChange(newImageId);
                            }}
                        />
                    )}
                />

                {/* Link type */}
                <SectionLabel required>
                    <FormattedMessage id="brandPromotionalBanner.modal.link.label" />
                </SectionLabel>
                <Controller
                    name="linkType"
                    control={control}
                    render={({ field }) => (
                        <RadioCardGroup>
                            {linkTypeOptions.map((option) => (
                                <RadioCard
                                    key={option.value}
                                    selected={field.value === option.value}
                                    onClick={() => {
                                        field.onChange(option.value);
                                        setCurrentLinkType(option.value);
                                        if (option.value === 'none') {
                                            setValue('linkTargetRef', '');
                                        }
                                    }}
                                >
                                    <RadioDot selected={field.value === option.value} />
                                    <RadioCardContent>
                                        <RadioCardTitle>{computeText(intl, option.labelKey)}</RadioCardTitle>
                                        <RadioCardDescription>{computeText(intl, option.descKey)}</RadioCardDescription>
                                    </RadioCardContent>
                                </RadioCard>
                            ))}
                        </RadioCardGroup>
                    )}
                />

                {/* Link target ref (free text for SKU / category name) */}
                {currentLinkType === 'product' && (
                    <>
                        <Controller
                            name="linkTargetRef"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Input {...field} labelId="brandPromotionalBanner.modal.link.product" required />
                            )}
                        />
                        <AlertText>
                            <FormattedMessage id="brandPromotionalBanner.modal.link.product.hint" />
                        </AlertText>
                    </>
                )}

                {currentLinkType === 'category' && (
                    <>
                        <Controller
                            name="linkTargetRef"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Input {...field} labelId="brandPromotionalBanner.modal.link.category" required />
                            )}
                        />
                        <AlertText>
                            <FormattedMessage id="brandPromotionalBanner.modal.link.category.hint" />
                        </AlertText>
                    </>
                )}

                {/* Restaurant targets */}
                <SectionLabel>
                    <FormattedMessage id="brandPromotionalBanner.modal.restaurants.label" />
                </SectionLabel>
                {!hasSelectedRestaurants && (
                    <HelperText>
                        <FormattedMessage id="brandPromotionalBanner.modal.restaurants.empty" />
                    </HelperText>
                )}
                <MultiSelect<number>
                    isMulti
                    options={restaurantOptions}
                    value={selectedRestaurantOptions}
                    onChange={handleRestaurantChange}
                    placeholder={computeText(intl, 'brandPromotionalBanner.modal.restaurants.label')}
                    isOptionDisabled={isOptionDisabled}
                    formatOptionLabel={formatOptionLabel}
                    canSelectAll
                />
            </Modal>
        </>
    );
};
